import { Directive, ElementRef, Inject, NgZone, OnDestroy, OnInit, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';

/**
 * Keeps a tap on a carousel from sliding it.
 *
 * owl calls a touch a drag as soon as the finger has moved 3px - `_oneMouseTouchMove` in
 * ngx-owl-carousel-o - and from that moment it slides the stage AND blocks the link under the
 * finger for the rest of the gesture. A finger travels further than 3px on most taps, so tapping
 * a product card usually slid the carousel instead of opening the product, and took several
 * tries. Every platform allows a tap more room than that: Android's own limit is 8dp.
 *
 * So the first few pixels are held back: touchmove is kept from reaching owl's document listener
 * until the finger has travelled SLOP. A swipe passes that within the first moments and drags as
 * it always did, only starting from a few pixels further along. Nothing here calls
 * preventDefault, so the page still scrolls vertically through the carousel.
 */
@Directive({
    selector: '[appTouchSlop]'
})
export class TouchSlopDirective implements OnInit, OnDestroy {
    /** How far a finger may travel and still count as a tap, in pixels. */
    private static readonly SLOP = 12;

    private start: { x: number, y: number } = null;
    private dragging = false;
    private removers: (() => void)[] = [];

    constructor(
        @Inject(PLATFORM_ID) private platformId: any,
        private elementRef: ElementRef,
        private zone: NgZone,
    ) { }

    ngOnInit(): void {
        if (!isPlatformBrowser(this.platformId)) {
            return;
        }

        // Outside Angular: these fire on every frame of a swipe and change nothing that is rendered.
        this.zone.runOutsideAngular(() => {
            const element: HTMLElement = this.elementRef.nativeElement;

            this.on(element, 'touchstart', (event: TouchEvent) => {
                // A second finger is a pinch, not a tap: leave it to owl.
                this.dragging = event.touches.length !== 1;
                this.start = this.dragging ? null : this.point(event);
            });

            this.on(element, 'touchmove', (event: TouchEvent) => {
                if (this.dragging || !this.start || event.touches.length !== 1) {
                    return;
                }

                const moved = this.point(event);
                const distance = Math.sqrt(
                    Math.pow(moved.x - this.start.x, 2) + Math.pow(moved.y - this.start.y, 2)
                );

                if (distance < TouchSlopDirective.SLOP) {
                    // owl listens on document, so stopping it here is enough: it never learns of
                    // this move and the tap stays a tap.
                    event.stopPropagation();
                    return;
                }

                // Past the slop - a swipe. Everything from here reaches owl untouched.
                this.dragging = true;
            });

            const reset = () => {
                this.start = null;
                this.dragging = false;
            };

            this.on(element, 'touchend', reset);
            this.on(element, 'touchcancel', reset);
        });
    }

    ngOnDestroy(): void {
        this.removers.forEach(remove => remove());
        this.removers = [];
    }

    private on(element: HTMLElement, event: string, handler: (event: any) => void): void {
        // Passive: nothing here calls preventDefault, and the browser keeps scrolling smoothly.
        // Removal takes no such options - both listen on the bubble phase.
        element.addEventListener(event, handler, { passive: true });
        this.removers.push(() => element.removeEventListener(event, handler));
    }

    private point(event: TouchEvent): { x: number, y: number } {
        return { x: event.touches[0].clientX, y: event.touches[0].clientY };
    }
}
